import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { MeditationResults, MeditationResultItem } from '../components/meditation';
import { config } from '../config';
import { authService } from '../services/auth';
import SEO from '../components/common/SEO';

const Container = styled.div`
  margin: 0 auto;
  padding: 20px;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  color: white;
  padding: 20px 20px;
  border-radius: 12px;
  margin-bottom: 40px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 20px;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 30px;
  opacity: 0.9;
`;

const Button = styled(Link)`
  display: inline-block;
  background-color: #4CAF50;
  color: white;
  padding: 12px 24px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: #45a049;
  }
`;

const Section = styled.section`
  margin-bottom: 50px;
`;

const SectionHeader = styled.div`
  margin-bottom: 15px;
`;

const SectionTitleLink = styled(Link)`
  font-size: 1.8rem;
  margin: 0;
  color: #ffffff;
  text-decoration: none;
  display: inline-block;
  transition: color 0.2s ease;
  
  &:hover {
    color: #999;
    text-decoration: none;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1rem;
  padding: 40px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #ff6b6b;
  font-size: 1rem;
  padding: 40px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #999;
  font-size: 1rem;
  padding: 40px;
`;

const Home: React.FC = () => {
  const { isLoggedIn } = useUser();
  
  // State for three meditation categories
  const [followingMeditations, setFollowingMeditations] = useState<MeditationResultItem[]>([]);
  const [featuredMeditations, setFeaturedMeditations] = useState<MeditationResultItem[]>([]);
  const [newestMeditations, setNewestMeditations] = useState<MeditationResultItem[]>([]);
  
  // Loading states
  const [followingLoading, setFollowingLoading] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [newestLoading, setNewestLoading] = useState(true);
  
  // Error states
  const [followingError, setFollowingError] = useState<string | null>(null);
  const [featuredError, setFeaturedError] = useState<string | null>(null);
  const [newestError, setNewestError] = useState<string | null>(null);

  // Fetch following meditations (only if logged in)
  const fetchFollowingMeditations = async () => {
    if (!isLoggedIn) return;
    
    setFollowingLoading(true);
    setFollowingError(null);
    
    try {
      const response = await fetch(`${config.apiUrl}/scripts/following?limit=10`, {
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch following meditations');
      }
      
      const data = await response.json();
      setFollowingMeditations(data.meditations || []);
    } catch (error) {
      console.error('Error fetching following meditations:', error);
      setFollowingError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setFollowingLoading(false);
    }
  };

  // Fetch featured meditations
  const fetchFeaturedMeditations = async () => {
    setFeaturedLoading(true);
    setFeaturedError(null);
    
    try {
      const response = await fetch(`${config.apiUrl}/scripts/featured?limit=10`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch featured meditations');
      }
      
      const data = await response.json();
      setFeaturedMeditations(data.meditations || []);
    } catch (error) {
      console.error('Error fetching featured meditations:', error);
      setFeaturedError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setFeaturedLoading(false);
    }
  };

  // Fetch newest meditations
  const fetchNewestMeditations = async () => {
    setNewestLoading(true);
    setNewestError(null);
    
    try {
      const response = await fetch(`${config.apiUrl}/scripts/new?limit=10`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch newest meditations');
      }
      
      const data = await response.json();
      setNewestMeditations(data.meditations || []);
    } catch (error) {
      console.error('Error fetching newest meditations:', error);
      setNewestError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setNewestLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchFeaturedMeditations();
    fetchNewestMeditations();
  }, []);

  // Load following meditations when user logs in
  useEffect(() => {
    if (isLoggedIn) {
      fetchFollowingMeditations();
    } else {
      setFollowingMeditations([]);
      setFollowingError(null);
    }
  }, [isLoggedIn]);

  return (
    <>
      <SEO 
        title="YouMeditation - Create & Discover Personalized Meditation Experiences"
        description="Create custom meditation scripts with AI, discover guided meditations, or enjoy peaceful unguided sessions. Join our community of mindful practitioners."
        keywords="meditation app, guided meditation, custom meditation scripts, AI meditation, mindfulness, wellness, relaxation, meditation community"
        url="/"
      />
      <Container>
      {!isLoggedIn && (
        <Hero>
          <Title>Welcome to YouMeditation</Title>
          <Subtitle>
            YouMeditation is a user driven community for creating and sharing meditations. You can create your own meditations, listen to one of the many meditations created by others, or customize an existing meditation to your liking.
            <br />
            <br />
            There are powerful AI tools to help you create or modify meditations, and easy to use audio editing tools, including brainwave entrainment sound generators, and a library of music and sounds to choose from.
            <br />
            <br />
            Explore what's here, or create your perfect meditation today!
          </Subtitle>
          
        </Hero>
      )}

      {/* Following Meditations - Only show if logged in */}
      {isLoggedIn && (
        <Section>
          <SectionHeader>
            <SectionTitleLink to="/following">Following</SectionTitleLink>
          </SectionHeader>
          
          {followingLoading && <LoadingMessage>Loading meditations from people you follow...</LoadingMessage>}
          {followingError && <ErrorMessage>Error: {followingError}</ErrorMessage>}
          {!followingLoading && !followingError && followingMeditations.length === 0 && (
            <EmptyMessage>
              No meditations found from people you follow. Try following some creators!
            </EmptyMessage>
          )}
          {!followingLoading && !followingError && followingMeditations.length > 0 && (
            <MeditationResults meditations={followingMeditations} loading={false} />
          )}
        </Section>
      )}

      {/* Featured Meditations */}
      <Section>
        <SectionHeader>
          <SectionTitleLink to="/featured">Featured</SectionTitleLink>
        </SectionHeader>
        
        {featuredLoading && <LoadingMessage>Loading featured meditations...</LoadingMessage>}
        {featuredError && <ErrorMessage>Error: {featuredError}</ErrorMessage>}
        {!featuredLoading && !featuredError && featuredMeditations.length === 0 && (
          <EmptyMessage>No featured meditations available at this time.</EmptyMessage>
        )}
        {!featuredLoading && !featuredError && featuredMeditations.length > 0 && (
          <MeditationResults meditations={featuredMeditations} loading={false} />
        )}
      </Section>

      {/* Newest Meditations */}
      <Section>
        <SectionHeader>
          <SectionTitleLink to="/new">Newest</SectionTitleLink>
        </SectionHeader>
        
        {newestLoading && <LoadingMessage>Loading newest meditations...</LoadingMessage>}
        {newestError && <ErrorMessage>Error: {newestError}</ErrorMessage>}
        {!newestLoading && !newestError && newestMeditations.length === 0 && (
          <EmptyMessage>No new meditations available at this time.</EmptyMessage>
        )}
        {!newestLoading && !newestError && newestMeditations.length > 0 && (
          <MeditationResults meditations={newestMeditations} loading={false} />
        )}
      </Section>
    </Container>
    </>
  );
};

export default Home; 